import { useEffect, useState } from "react";
import {
  TableBody,
  TableRow,
  TableCell,
  Typography,
  makeStyles,
  Toolbar,
  InputAdornment,
} from "@material-ui/core";
import { Select, MenuItem, Pagination, Paper, Box } from "@mui/material";
import { AiOutlineSearch } from "react-icons/ai";
import { FiEdit } from "react-icons/fi";
import Controls from "../../../components/controls/Controls";
import useTable from "../../../components/customHelpers/usePaginationTable";
import Loading from "../../../components/customHelpers/Loading";
import Norecords from "../../../components/customHelpers/Norecords";
import Popup from "../../../components/customHelpers/Popup";
import UpdateContainer from "../../../components/forms/updateContainer";
import Product from "../../../actions/product";

const productHeadCells = [
  { id: "name", label: "Name" },
  { id: "quantity", label: "Quantity" },
  { id: "price", label: "Price" },
  { id: "productName", label: "Product Name" },
  { id: "sku", label: "Sku" },
  { id: "store", label: "Store" },
  { id: "createdAt", label: "Date" },
  { id: "actions", label: "Actions", disableSorting: true },
];

const useStyles = makeStyles((theme) => ({
  pageContent: {
    margin: theme.spacing(5),
    padding: theme.spacing(3),
  },
  searchInput: {
    marginTop: theme.spacing(1),
    width: "75%",
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minHeight: "48px",
  },
  paginationContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "end",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  pageSizeSelect: {
    minWidth: 120,
    marginRight: theme.spacing(2),
  },
}));

const ProductContainers = ({ NotifyMessage }) => {
  const classes = useStyles();
  const { viewAllProductsByContainerWithPages } = Product();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [actualTotal, setActualTotal] = useState(0);
  const [tot, setTot] = useState(1);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [edite, setEdit] = useState(false);
  const pageSizes = [15, 25, 50, 100];

  const [filterFn, setFilterFn] = useState({
    fn: (items) => items,
  });

  useEffect(() => {
    getProductsByContainer();
  }, [page, limit, searchQuery]);

  const getProductsByContainer = () => {
    setLoading(true);
    viewAllProductsByContainerWithPages(page, limit, searchQuery, null).then(
      (data) => {
        if (data.err) {
          NotifyMessage({
            message: data.err,
            type: "error",
          });
        } else if (data.product) {
          setContainers(data.product);
          setTotalPages(Math.ceil(data.total / limit));
          setActualTotal(data.pagination.total);
          setTot(data.pagination.totalPages);
        }
        setLoading(false);
      }
    );
  };

  const handleSearch = (e) => {
    setPage(1);
    setSearchQuery(e.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setLimit(event.target.value);
    setPage(1);
  };

  const {
    TblContainer,
    TblHead,
    recordsAfterSortingAndFiltering,
  } = useTable(containers, productHeadCells, filterFn);

  const startIndex = (page - 1) * limit + 1;
  const endIndex = Math.min(startIndex + limit - 1, actualTotal);

  return (
    <>
      <Toolbar className={classes.toolbar}>
        <Controls.Input
          label="Search Product Containers"
          className={classes.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AiOutlineSearch />
              </InputAdornment>
            ),
          }}
          onChange={handleSearch}
        />
      </Toolbar>
      {loading ? (
        <Loading />
      ) : (
        <Paper style={{ overflowX: "auto" }}>
          <TblContainer>
            <TblHead />
            <TableBody>
              {recordsAfterSortingAndFiltering().length > 0 ? (
                recordsAfterSortingAndFiltering().map((item) => (
                  <TableRow key={item.Id}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.productList?.price}</TableCell>
                    <TableCell>{item.productList?.product.name}</TableCell>
                    <TableCell>{item.productList?.product.sku}</TableCell>
                    <TableCell>{item.productList?.store.locationName}</TableCell>
                    <TableCell>{new Date(item.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <Controls.ActionButton
                        color="primary"
                        onClick={() => {
                          setOpenPopup(true);
                          setRecordForEdit(item);
                          setEdit(true);
                        }}
                      >
                        <FiEdit />
                      </Controls.ActionButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <Norecords col={8} />
              )}
            </TableBody>
          </TblContainer>
          <Box className={classes.paginationContainer}>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" style={{ marginRight: 8 }}>
                Rows per page:
              </Typography>
              <Select
                value={limit}
                onChange={handlePageSizeChange}
                className={classes.pageSizeSelect}
                size="small"
              >
                {pageSizes.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {actualTotal && (
              <Typography variant="body2">
                {startIndex}-{endIndex} of {actualTotal}
              </Typography>
            )}
            {containers.length > 0 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                hideNextButton={page === tot}
                hidePrevButton={page === 1}
                shape="rounded"
                siblingCount={1}
              />
            )}
          </Box>
        </Paper>
      )}
      <Popup
        title="Product Container Form"
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
      >
        <UpdateContainer
          openPopup={openPopup}
          NotifyMessage={NotifyMessage}
          setOpenPopup={setOpenPopup}
          recordForEdit={recordForEdit}
          getProductsByContainer={getProductsByContainer}
          isProductContainer={true}
        />
      </Popup>
    </>
  );
};

export default ProductContainers;